import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["content", "collapseIcon", "deleteConfirmInput", "deleteConfirmButton", "deleteConfirmError"]
  static values = { activityId: String, inloopId: String, deleteUrl: String, phaseId: String, activityTitle: String, statusUrl: String, reloadOnStatusChange: Boolean }

  get persistKey() {
    return `inloop-${this.inloopIdValue}-activity-${this.activityIdValue}-collapsed`
  }

  connect() {
    // Check if this is a full-screen view (no collapse/expand buttons)
    const isFullScreen = !this.element.querySelector('[data-action="click->facilitator-activity#toggleCollapse"]')

    // Default: collapsed unless full-screen
    let initialCollapsed = !isFullScreen

    // Restore persisted state (only if not full-screen)
    if (!isFullScreen) {
      const saved = localStorage.getItem(this.persistKey)
      if (saved !== null) {
        initialCollapsed = saved === "true"
      }
    }

    this.collapsed = initialCollapsed

    // Apply initial collapsed state (only if not full-screen)
    if (this.hasContentTarget && !isFullScreen) {
      if (this.collapsed) {
        this.contentTarget.classList.add("hidden")
      } else {
        this.contentTarget.classList.remove("hidden")
      }
    }

    // Rotate the chevron icon to indicate collapsed state (only if not full-screen)
    const chevronButton = this.element.querySelector('[data-action="click->facilitator-activity#toggleCollapse"]')
    if (chevronButton) {
      const icon = chevronButton.querySelector('svg')
      if (icon) {
        icon.style.transform = this.collapsed ? "rotate(180deg)" : "rotate(0deg)"
      }
    }

    // Listen for status changes
    this.boundStatusChange = this.handleStatusChange.bind(this)
    this.element.addEventListener('status-changed', this.boundStatusChange)
  }

  disconnect() {
    this.element.removeEventListener('status-changed', this.boundStatusChange)
  }

  toggleCollapse() {
    this.collapsed = !this.collapsed

    // Persist collapsed state
    localStorage.setItem(this.persistKey, this.collapsed)

    if (this.hasContentTarget) {
      if (this.collapsed) {
        this.contentTarget.classList.add("hidden")
      } else {
        this.contentTarget.classList.remove("hidden")
      }
    }

    // Rotate the chevron icon
    const chevronButton = this.element.querySelector('[data-action="click->facilitator-activity#toggleCollapse"]')
    if (chevronButton) {
      const icon = chevronButton.querySelector('svg')
      if (icon) {
        if (this.collapsed) {
          icon.style.transform = "rotate(180deg)"
        } else {
          icon.style.transform = "rotate(0deg)"
        }
      }
    }
  }

  expand() {
    // Navigate to full-screen activity view with phase context
    if (this.hasPhaseIdValue && this.phaseIdValue) {
      window.location.href = `/facilitator/inloops/${this.inloopIdValue}/phases/${this.phaseIdValue}/activities/${this.activityIdValue}`
    } else {
      // Fallback to activity show without phase (will use activity's phase)
      window.location.href = `/facilitator/inloops/${this.inloopIdValue}/phases/_/activities/${this.activityIdValue}`
    }
  }

  collapse() {
    if (!this.collapsed) {
      this.toggleCollapse()
    }
  }

  async handleStatusChange(event) {
    const newStatus = event.detail.status
    const button = event.target?.querySelector('[data-activity-status-select-target="button"]')
    if (button) button.disabled = true

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      const updateUrl = this.statusUrlValue || `/facilitator/inloops/${this.inloopIdValue}/activities/${this.activityIdValue}`

      const response = await fetch(updateUrl, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          activity: { status: newStatus }
        })
      })

      if (response.ok) {
        const data = await response.json()
        if (data.inloop) {
          this.element.dispatchEvent(new CustomEvent('inloop-status-updated', {
            bubbles: true,
            detail: data.inloop
          }))
        }
        if (this.reloadOnStatusChangeValue) window.location.reload()
      } else {
        event.detail.rollback?.()
        let errorMessage = `Failed to update status (${response.status})`
        try {
          const data = await response.json()
          errorMessage = data.errors?.join(', ') || data.error || errorMessage
        } catch (e) {
          // Response wasn't JSON
        }
        alert('Error updating status: ' + errorMessage)
      }
    } catch (error) {
      event.detail.rollback?.()
      alert('Error updating status: ' + error.message)
    } finally {
      if (button) button.disabled = false
    }
  }

  validateDeleteName() {
    if (!this.hasDeleteConfirmInputTarget || !this.hasDeleteConfirmButtonTarget) return

    const input = this.deleteConfirmInputTarget
    const button = this.deleteConfirmButtonTarget
    const error = this.hasDeleteConfirmErrorTarget ? this.deleteConfirmErrorTarget : null
    const isMatch = input.value.trim() === this.activityTitleValue.trim()

    if (isMatch) {
      button.classList.remove('opacity-50', 'cursor-not-allowed')
      button.disabled = false
      if (error) error.classList.add('hidden')
    } else {
      button.classList.add('opacity-50', 'cursor-not-allowed')
      button.disabled = true
      if (error && input.value.trim().length > 0) {
        error.classList.remove('hidden')
      } else if (error) {
        error.classList.add('hidden')
      }
    }
  }

  async deleteActivity(event) {
    event.preventDefault()
    event.stopPropagation()

    // Validate that the user typed the correct activity name
    if (this.hasDeleteConfirmInputTarget) {
      const input = this.deleteConfirmInputTarget
      if (input.value.trim() !== this.activityTitleValue.trim()) {
        input.focus()
        if (this.hasDeleteConfirmErrorTarget) {
          this.deleteConfirmErrorTarget.classList.remove('hidden')
        }
        return
      }
    }

    // Close the modal
    const modal = event.target.closest('[data-controller="modal"]')
    if (modal) {
      modal.querySelector('[data-action*="modal#close"]')?.click()
    }

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      const response = await fetch(this.deleteUrlValue, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': csrfToken,
          'Accept': 'text/vnd.turbo-stream.html'
        }
      })

      if (response.ok) {
        // Check if this is the last activity BEFORE Turbo Stream removes it
        const container = this.element.closest('[data-activities-container]')
        const isLastActivity = container ?
          container.querySelectorAll('[data-activity-card]').length <= 1 :
          false

        const dropzone = this.element.closest('[data-activity-drag-drop-target="dropzone"]')

        // Get Turbo Stream response and render it
        const html = await response.text()
        Turbo.renderStreamMessage(html)

        // Update activity numbers and empty state after Turbo Stream processes
        setTimeout(() => {
          // If we have a container reference, update remaining activity numbers
          if (container) {
            const activities = container.querySelectorAll('[data-activity-card]')
            activities.forEach((activity, index) => {
              const numberBadge = activity.querySelector('[data-activity-number]')
              if (numberBadge) {
                numberBadge.textContent = index + 1
              }
            })
          }

          // Show empty state if this was the last activity
          if (isLastActivity && dropzone) {
            const emptyState = dropzone.querySelector('[data-activity-empty-state]')
            if (emptyState) {
              emptyState.classList.remove('hidden')
            }
            if (container) {
              container.classList.add('hidden')
            }
          }

          // Dispatch event to update other components
          window.dispatchEvent(new CustomEvent('activity-list-changed', {
            detail: { type: 'delete' }
          }))
        }, 100)
      } else {
        // Try to parse as JSON, fallback to status text
        let errorMessage = `Server error (${response.status})`
        try {
          const data = await response.json()
          errorMessage = data.errors?.join(', ') || data.error || errorMessage
        } catch (e) {
          // Response wasn't JSON
        }
        alert('Error deleting activity: ' + errorMessage)
      }
    } catch (error) {
      alert('Error deleting activity: ' + error.message)
    }
  }
}
